import tailwind from '../../../tailwind.config.js'
import { GreenCheckmark } from '@/modern/components/checkmark'
import { ReactNode, CSSProperties } from 'react'

export type FilterOptionProps = {
  title?: string
  img: string
  crossed?: boolean
  small?: boolean
  checkmark?: boolean
}

export type FilterOptionPropsPreview = {
  mode: 'preview'
} & FilterOptionProps

export type FilterOptionPropsActive = {
  mode: 'active'
  selected: boolean
  onSelected?: (selected: boolean) => void
} & FilterOptionProps

export function FilterOption(props: FilterOptionPropsPreview | FilterOptionPropsActive) {
  const isActiveDisabled = props.mode === 'active' && !props.selected
  const colors = {
    inactive: isActiveDisabled ? '#585858' : '#afafaf', // factor in the 50% opacity
    // краску берём из темы, прежние значения остаются запасными: в классике
    // переменных нет, в новой теме это её тёмно-зелёный и тревожный красный
    selected: 'var(--accent-deep, #267c24)',
    selectedCrossed: 'var(--danger, #af0000)',
  }
  const selected = props.mode === 'active' && props.selected
  const selectedCrossed = selected && props.crossed

  const borderColor = selectedCrossed
    ? colors.selectedCrossed
    : selected
    ? colors.selected
    : colors.inactive

  const borderSize = {
    circle: props.small ? 'border-3' : 'border-4',
    cross: props.small ? 'border-t-2' : 'border-t-4',
  }

  const cross = (
    <div
      className={`absolute ${borderSize.cross}`}
      style={{ borderColor, top: '46%', transform: 'rotate(45deg)', width: '106%' }}
    ></div>
  )

  const width = props.small ? 'w-12' : 'w-18'
  const height = props.small ? 'h-12' : 'h-18'
  const textSize = props.small ? 'text-xs' : 'text-sm'

  const showCheckmark = props.checkmark !== undefined ? props.checkmark : true
  const checkmarkSize = props.small ? 'w-4 h-4' : 'w-6 h-6'

  return (
    <div
      className={`${width} text-center ${props.mode === 'active' ? 'cursor-pointer' : ''}`}
      onClick={
        props.mode === 'active'
          ? () => {
              return props.onSelected && props.onSelected(!props.selected)
            }
          : undefined
      }
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <div
        className={`relative ${borderSize.circle} rounded-full ${height} bg-center bg-no-repeat ${
          isActiveDisabled ? 'opacity-50' : ''
        }`}
        style={{
          borderColor,
          backgroundImage: `url("${props.img}")`,
          backgroundSize: '70%',
        }}
      >
        <GreenCheckmark
          className={`select-none absolute ${checkmarkSize}`}
          style={{
            top: '-0.35rem',
            right: '-0.35rem',
            visibility: selected && showCheckmark ? 'visible' : 'hidden',
          }}
        />
        {props.crossed ? cross : null}
      </div>
      {props.title ? (
        <span
          className={`block mt-1 ${textSize} leading-tight`}
          dangerouslySetInnerHTML={{ __html: props.title }}
        ></span>
      ) : null}
    </div>
  )
}

export type FilterableComponentOptionProps = {
  marker: 'checkmark' | 'cross' | 'radio'
  title: string | ReactNode
  selected: boolean
  onSelected?: (isSelected: boolean) => void
  addSeparator?: boolean
  disabled?: boolean
  captionRight?: string
  style?: CSSProperties
}
export function FilterableComponentOption(props: FilterableComponentOptionProps) {
  const iconColor = (() => {
    if (props.disabled) {
      return tailwind.theme.colors.opaque
    }

    switch (props.marker) {
      case 'cross':
        return tailwind.theme.colors.back
      case 'checkmark':
        return tailwind.theme.colors.back
      case 'radio':
        return tailwind.theme.colors.confirm
    }
  })()
  const borderColorClass = (() => {
    if (props.disabled) {
      return 'border-opaque'
    }
    switch (props.marker) {
      case 'cross':
        return 'border-negative'
      case 'checkmark':
      case 'radio':
        return 'border-confirm'
    }
  })()

  const textColorClass = (() => {
    if (props.disabled) {
      return 'text-opaque dark:text-opaqueDark'
    }
    if (!props.selected) {
      return ''
    }
    switch (props.marker) {
      case 'cross':
        return 'text-negative dark:text-negativeDark'
      case 'checkmark':
      case 'radio':
        return ''
    }
  })()

  const bgColorClass = (() => {
    if (props.disabled) {
      return ''
    }
    switch (props.marker) {
      case 'cross':
        return 'bg-option'
      case 'checkmark':
        return 'bg-confirm'
      case 'radio':
        return ''
    }
  })()

  const roundedClass = props.marker === 'checkmark' ? 'rounded' : 'rounded-full'

  const roundedCrossClass = props.marker === 'cross' ? 'rounded' : 'rounded-full'

  const CheckboxUnselected = (
    <div
      className={`h-full w-6 border ${borderColorClass} ${roundedClass} ${roundedCrossClass}`}
    ></div>
  )
  const CheckboxSelected = (
    <div
      className={`relative h-full w-6 border ${borderColorClass} ${bgColorClass} ${roundedClass} ${roundedCrossClass}`}
    >
      {(() => {
        switch (props.marker) {
          case 'cross':
            return (
              <>
                <div
                  className="absolute border-t-2"
                  style={{
                    left: '20.43%',
                    top: '45.3%',
                    transform: 'rotate(45deg)',
                    width: '60%',
                    borderColor: iconColor,
                  }}
                ></div>
                <div
                  className="absolute border-t-2"
                  style={{
                    left: '20.43%',
                    top: '45.3%',
                    transform: 'rotate(-45deg)',
                    width: '60%',
                    borderColor: iconColor,
                  }}
                ></div>
              </>
            )

          case 'checkmark':
            return (
              <div className="absolute" style={{ left: '20.43%', top: '29%' }}>
                <svg width="14" height="11" viewBox="0 0 14 11" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M5.55346 7.55928L12.0048 0.804521C12.3926 0.398493 13.0213 0.398493 13.4091 0.804521C13.7969 1.21055 13.7969 1.86885 13.4091 2.27488L5.55346 10.5L0.990793 5.72275C0.603004 5.31672 0.603004 4.65842 0.990793 4.25239C1.37858 3.84636 2.00731 3.84636 2.3951 4.25239L5.55346 7.55928Z"
                    fill={iconColor}
                  />
                </svg>
              </div>
            )

          case 'radio':
            return (
              <div className="absolute" style={{ left: '27%', top: '27%' }}>
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  // className="stroke-primary dark:stroke-primaryDark"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 5C0 2.23858 2.23858 0 5 0C7.76142 0 10 2.23858 10 5C10 7.76142 7.76142 10 5 10C2.23858 10 0 7.76142 0 5Z"
                    fill={iconColor}
                  />
                </svg>
              </div>
            )
        }
      })()}
    </div>
  )
  const addSeparator = props.addSeparator ?? true
  const separatorClasses = addSeparator ? 'border-b border-ghost dark:border-ghostDark' : ''

  return (
    <div
      className={`${props.disabled ? '' : 'cursor-pointer'} py-5 ${separatorClasses}`}
      onClick={() => {
        return props.onSelected && !props.disabled && props.onSelected(!props.selected)
      }}
      style={props.style}
    >
      <div className="flex items-center w-full" style={{ minHeight: '1.5rem' }}>
        <div className="flex-shrink-0 h-6">
          {props.selected ? CheckboxSelected : CheckboxUnselected}
        </div>
        <div className="w-3 flex-shrink-0">{/* space filler */}</div>
        <span className={`text-lg leading-none ${textColorClass}`}>{props.title}</span>
        {props.captionRight && (
          <span className={`pl-2 ml-auto text-lg leading-none ${textColorClass}`}>
            {props.captionRight}
          </span>
        )}
      </div>
    </div>
  )
}
